import type { MediaState, PlayableFile } from './media.js';

/** Ranga w pokoju. Liczby, bo porównania >= są czytelniejsze niż tablica uprawnień. */
export const RoomRank = {
  Member: 0,
  Moderator: 1,
  Admin: 2,
  Owner: 3,
} as const;

export type RoomRankValue = (typeof RoomRank)[keyof typeof RoomRank];

/** Rola nadawana zaproszeniem. Viewer nie może zostać leaderem. */
export const INVITE_ROLES = ['viewer', 'controller'] as const;
export type InviteRole = (typeof INVITE_ROLES)[number];

export interface RoomMemberView {
  /** user.id dla zalogowanych, `guest:<socketId>` dla gości. Klucz przywództwa. */
  userKey: string;
  displayName: string;
  avatarUrl: string | null;
  isGuest: boolean;
  rank: RoomRankValue;
  role: InviteRole;
  muted: boolean;
  isLeader: boolean;
}

/**
 * Zgodność jednego uczestnika z odtwarzaniem w pokoju.
 *
 * Wyliczana po stronie SERWERA w chwili zgłoszenia, nie u odbiorcy: tylko serwer
 * zna wspólny czas odniesienia, a przeliczanie u każdego z osobna dodawałoby do
 * wyniku jego własne przesunięcie zegara — czyli mierzyłoby dwie rzeczy naraz.
 */
export interface MemberSyncView {
  userKey: string;
  /**
   * Odchylenie w sekundach: dodatnie znaczy „jest dalej niż powinien".
   * `null`, gdy uczestnik nic jeszcze nie zgłosił (np. wciąż wczytuje).
   */
  driftSec: number | null;
  /** Czy u niego materiał idzie. `null` przy braku zgłoszenia. */
  playing: boolean | null;
  /** Ile milisekund temu przyszło ostatnie zgłoszenie. */
  reportedAgoMs: number | null;
}

export interface RoomQueueItemView {
  uid: string;
  position: number;
  kind: 'episode' | 'upload';
  titleId: string | null;
  titleLabel: string;
  episodeNumber: number | null;
  mediaFileId: string | null;
  /** Stan materiału — pokój czeka, dopóki nie jest 'ready'. */
  mediaState: MediaState;
  durationSec: number | null;
  thumbnailUrl: string | null;
  addedByDisplayName: string;
  addedAt: string;
}

export interface RoomSnapshot {
  /**
   * Klucz tożsamości ODBIORCY tej migawki.
   *
   * Podaje go serwer, bo przeglądarka go nie zna — dla zalogowanych to
   * identyfikator konta, dla gościa klucz wiązany z gniazdem. Bez tego pola
   * klient musiałby zgadywać, który wpis na liście uczestników jest jego,
   * a przy dwóch osobach wchodzących naraz zgadłby źle.
   */
  youUserKey: string;
  slug: string;
  name: string;
  description: string | null;
  isPublic: boolean;
  isTemporary: boolean;
  expiresAt: string | null;
  leaderUserKey: string | null;
  members: RoomMemberView[];
  queue: RoomQueueItemView[];
  current: {
    queueItem: RoomQueueItemView;
    /** Obecne dopiero gdy mediaState === 'ready'. */
    file: PlayableFile | null;
    time: number;
    paused: boolean;
    emittedAt: number | null;
  } | null;
  serverNow: number;
}
